import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Alert, 
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  Snackbar
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useLanguage } from '../contexts/LanguageContext';
import { translateConnectionOption, translateRaceEthnicityOption, getAuthErrorMessage, Translations } from '../i18n';
import { api } from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `auth-tab-${index}`,
    'aria-controls': `auth-tabpanel-${index}`,
  };
}

// Connection options for different tenants
const getConnectionOptions = (tenantId?: string) => {
  switch (tenantId) {
    case 'evanston':
      return [
        'Current Evanston resident',
        'Former Evanston resident',
        'Work in Evanston',
        'Own a business in Evanston',
        'Attend a faith community in Evanston',
        'Parent/guardian of an Evanston student',
        'Other'
      ];
    case 'severance':
    default:
      return [
        'Resident of Cleveland Heights',
        'Resident of University Heights',
        'Regular visitor to Severance',
        'Business owner in the area',
        'Employee in the area',
        'Other'
      ];
  }
};

const RACE_ETHNICITY_OPTIONS = [
  'Black or African American',
  'White',
  'Hispanic or Latino',
  'Asian',
  'Native American or Alaska Native',
  'Native Hawaiian or Other Pacific Islander',
  'Multiracial',
  'Prefer not to answer',
  'Other'
];

// Evanston-only demographic fields, shared by the signup, complete-profile and
// update-profile blocks. idSuffix keeps the select label ids unique per block.
interface EvanstonFieldsProps {
  idSuffix: string;
  t: Translations;
  disabled: boolean;
  required?: boolean;
  zipCode: string;
  setZipCode: (value: string) => void;
  ward: string;
  setWard: (value: string) => void;
  address: string;
  setAddress: (value: string) => void;
  raceEthnicity: string;
  setRaceEthnicity: (value: string) => void;
  otherRaceEthnicity: string;
  setOtherRaceEthnicity: (value: string) => void;
}

const EvanstonFields: React.FC<EvanstonFieldsProps> = ({
  idSuffix,
  t,
  disabled,
  required = false,
  zipCode,
  setZipCode,
  ward,
  setWard,
  address,
  setAddress,
  raceEthnicity,
  setRaceEthnicity,
  otherRaceEthnicity,
  setOtherRaceEthnicity,
}) => (
  <>
    <TextField
      label={t.loginForm.zipCode}
      fullWidth
      margin="normal"
      value={zipCode}
      onChange={(e) => setZipCode(e.target.value)}
      disabled={disabled}
      required={required}
      inputProps={{ maxLength: 5, pattern: '[0-9]{5}' }}
      helperText={t.loginForm.zipCodeHelper}
    />

    <FormControl fullWidth margin="normal" required={required}>
      <InputLabel id={`ward-label-${idSuffix}`}>{t.loginForm.ward}</InputLabel>
      <Select
        labelId={`ward-label-${idSuffix}`}
        value={ward}
        label={t.loginForm.ward}
        onChange={(e) => setWard(e.target.value)}
        disabled={disabled}
      >
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((wardNumber) => (
          <MenuItem key={wardNumber} value={wardNumber}>
            {t.loginForm.wardPrefix} {wardNumber}
          </MenuItem>
        ))}
        <MenuItem value="Unsure">{t.loginForm.unsure}</MenuItem>
        <MenuItem value="Other/Outside Evanston">{t.loginForm.wardOutside}</MenuItem>
      </Select>
    </FormControl>

    <TextField
      label={t.loginForm.address}
      fullWidth
      margin="normal"
      value={address}
      onChange={(e) => setAddress(e.target.value)}
      disabled={disabled}
      helperText={t.loginForm.addressHelper}
    />

    <FormControl fullWidth margin="normal" required={required}>
      <InputLabel id={`race-label-${idSuffix}`}>{t.loginForm.raceEthnicity}</InputLabel>
      <Select
        labelId={`race-label-${idSuffix}`}
        value={raceEthnicity}
        label={t.loginForm.raceEthnicity}
        onChange={(e) => setRaceEthnicity(e.target.value)}
        disabled={disabled}
      >
        {RACE_ETHNICITY_OPTIONS.map((option) => (
          <MenuItem key={option} value={option}>
            {translateRaceEthnicityOption(t, option)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    {raceEthnicity === 'Other' && (
      <TextField
        label={t.loginForm.pleaseSpecify}
        fullWidth
        margin="normal"
        value={otherRaceEthnicity}
        onChange={(e) => setOtherRaceEthnicity(e.target.value)}
        disabled={disabled}
        required={required}
      />
    )}

    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      {t.loginForm.demographicDisclaimer}
    </Typography>
  </>
);

interface LoginFormProps {
  onClose?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [connection, setConnection] = useState('');
  const [otherConnection, setOtherConnection] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [ward, setWard] = useState('');
  const [address, setAddress] = useState('');
  const [raceEthnicity, setRaceEthnicity] = useState('');
  const [otherRaceEthnicity, setOtherRaceEthnicity] = useState('');
  const [tabValue, setTabValue] = useState(0); // Default to login so returning users are not pushed into signup
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [isFirstTimeProfileUpdate, setIsFirstTimeProfileUpdate] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  const { login, signup, loginWithGoogle, resetPassword, isAuthenticated, currentUser, userProfile, logout, updateUserProfile } = useAuth();
  const { tenantConfig } = useTenant();
  const { t } = useLanguage();

  // Get tenant-specific connection options
  const connectionOptions = getConnectionOptions(tenantConfig?.tenant_id);

  // Populate fields if user is authenticated and check if profile completion is needed
  useEffect(() => {
    if (isAuthenticated && userProfile) {
      setFullName(userProfile.displayName || '');
      setConnection(userProfile.connection || '');
      setZipCode(userProfile.zip_code || '');
      setWard(userProfile.ward || '');
      setAddress(userProfile.address || '');
      const storedRace = userProfile.race_ethnicity || '';
      if (storedRace && !RACE_ETHNICITY_OPTIONS.includes(storedRace)) {
        // Free-text value entered via the "Other" option
        setRaceEthnicity('Other');
        setOtherRaceEthnicity(storedRace);
      } else {
        setRaceEthnicity(storedRace);
        setOtherRaceEthnicity('');
      }

      // Show profile completion if user is missing connection info (common for Google signups)
      // For Evanston tenant, also check for zip code and race/ethnicity (address stays optional)
      const needsProfileCompletion = !userProfile.connection || userProfile.connection.trim() === '' ||
        (tenantConfig?.tenant_id === 'evanston' && (!userProfile.zip_code || userProfile.zip_code.trim() === '' ||
          !userProfile.race_ethnicity || userProfile.race_ethnicity.trim() === ''));
      setShowProfileCompletion(needsProfileCompletion);
      setIsFirstTimeProfileUpdate(needsProfileCompletion);
    }
  }, [isAuthenticated, userProfile, tenantConfig?.tenant_id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
    setSuccess(null);
  };

  const handleConnectionChange = (event: SelectChangeEvent<string>) => {
    setConnection(event.target.value);
  };

  const validateForm = (isSignUp: boolean) => {
    if (!email.trim()) {
      setError(t.loginForm.emailRequired);
      return false;
    }

    if (!password) {
      setError(t.loginForm.passwordRequired);
      return false;
    }

    if (password.length < 6) {
      setError(t.loginForm.passwordMinLength);
      return false;
    }

    if (isSignUp) {
      if (!fullName) {
        setError(t.loginForm.fullNameRequired);
        return false;
      }

      if (!connection) {
        const projectName = tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center';
        setError(`${t.loginForm.connectionRequiredPrefix} ${projectName}`);
        return false;
      }

      if (connection === 'Other' && !otherConnection) {
        const projectName = tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center';
        setError(`${t.loginForm.connectionSpecifyPrefix} ${projectName}`);
        return false;
      }

      // Evanston-specific validation
      if (tenantConfig?.tenant_id === 'evanston') {
        if (!zipCode) {
          setError(t.loginForm.zipRequired);
          return false;
        }

        if (!/^\d{5}$/.test(zipCode)) {
          setError(t.loginForm.zipFormat);
          return false;
        }

        if (!ward) {
          setError(t.loginForm.wardRequired);
          return false;
        }

        if (!raceEthnicity) {
          setError(t.loginForm.raceEthnicityRequired);
          return false;
        }

        if (raceEthnicity === 'Other' && !otherRaceEthnicity) {
          setError(t.loginForm.raceEthnicitySpecifyRequired);
          return false;
        }
      }
    }

    return true;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm(false)) return;
    
    try {
      setIsSubmitting(true);
      setError(null);

      const user = await login(email.trim(), password);
      const token = await user.getIdToken();
      api.setAuthToken(token);

      setSuccess(t.loginForm.loginSuccess);
    } catch (err: any) {
      console.error('Login error:', err);
      setError(getAuthErrorMessage(t, err, t.loginForm.loginFailed));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    const cleanEmail = email.trim();
    if (!cleanEmail) {
      setError(t.loginForm.emailRequired);
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      await resetPassword(cleanEmail);
      // Deliberately vague so the message never confirms whether an account exists
      setSuccess(t.loginForm.resetEmailSent);
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(getAuthErrorMessage(t, err, t.loginForm.resetFailed));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm(true)) return;
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Create profile object
      const connectionValue = connection === 'Other' ? otherConnection : connection;
      const profile: any = {
        displayName: fullName,
        connection: connectionValue
      };
      
      // Add Evanston-specific fields if applicable
      if (tenantConfig?.tenant_id === 'evanston') {
        profile.zip_code = zipCode;
        profile.ward = ward;
        profile.race_ethnicity = raceEthnicity === 'Other' ? otherRaceEthnicity : raceEthnicity;
        if (address.trim()) {
          profile.address = address.trim();
        }
      }

      // Call signup with profile info
      const user = await signup(email.trim(), password, profile);
      const token = await user.getIdToken();
      api.setAuthToken(token);

      setSuccess(t.loginForm.accountCreated);
    } catch (err: any) {
      console.error('Signup error:', err);

      setError(getAuthErrorMessage(t, err, t.loginForm.signupFailed));
      // The account already exists, so send the user to the login form
      if (err.code === 'auth/email-already-in-use') {
        setTabValue(0);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Google Sign-In for login tab (no validation needed)
  const handleGoogleSignIn = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      await loginWithGoogle();
      setSuccess(t.loginForm.loginSuccess);
    } catch (err: any) {
      console.error('Google Sign-in error:', err);
      setError(getAuthErrorMessage(t, err, t.loginForm.googleSignInFailed));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Google Sign-Up for signup tab (with validation)
  const handleGoogleSignUp = async () => {
    // Note: Full name is NOT required for Google sign-up since it comes from Google account
    
    // Validate connection
    if (!connection) {
      const projectName = tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center';
      setSnackbarMessage(`${t.loginForm.connectionRequiredPrefix} ${projectName} ${t.loginForm.connectionBeforeGoogleSuffix}`);
      setSnackbarOpen(true);
      return;
    }

    if (connection === 'Other' && !otherConnection) {
      const projectName = tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center';
      setSnackbarMessage(`${t.loginForm.connectionSpecifyPrefix} ${projectName}`);
      setSnackbarOpen(true);
      return;
    }

    // Evanston-specific validation
    if (tenantConfig?.tenant_id === 'evanston') {
      if (!zipCode) {
        setSnackbarMessage(t.loginForm.zipBeforeGoogle);
        setSnackbarOpen(true);
        return;
      }

      if (!/^\d{5}$/.test(zipCode)) {
        setSnackbarMessage(t.loginForm.zipFormat);
        setSnackbarOpen(true);
        return;
      }

      if (!ward) {
        setSnackbarMessage(t.loginForm.wardBeforeGoogle);
        setSnackbarOpen(true);
        return;
      }

      if (!raceEthnicity) {
        setSnackbarMessage(t.loginForm.raceEthnicityBeforeGoogle);
        setSnackbarOpen(true);
        return;
      }

      if (raceEthnicity === 'Other' && !otherRaceEthnicity) {
        setSnackbarMessage(t.loginForm.raceEthnicitySpecifyRequired);
        setSnackbarOpen(true);
        return;
      }
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Proceed with Google sign-in (this creates the profile in the backend)
      const user = await loginWithGoogle();
      
      // Get auth token for API call
      const token = await user.getIdToken();
      api.setAuthToken(token);
      
      // Prepare profile data
      const connectionValue = connection === 'Other' ? otherConnection : connection;
      const profileData: any = {
        // Use Google-provided display name, or fallback to fullName if manually entered
        displayName: user.displayName || fullName || user.email?.split('@')[0] || 'User',
        connection: connectionValue
      };
      
      // Add Evanston-specific fields if applicable
      if (tenantConfig?.tenant_id === 'evanston') {
        profileData.zip_code = zipCode;
        profileData.ward = ward;
        profileData.race_ethnicity = raceEthnicity === 'Other' ? otherRaceEthnicity : raceEthnicity;
        if (address.trim()) {
          profileData.address = address.trim();
        }
      }

      // Wait for profile to exist, then update it (with retries)
      let retries = 5;
      let profileUpdated = false;
      
      while (retries > 0 && !profileUpdated) {
        try {
          // Try to update the profile
          await api.updateUserProfile(user.uid, profileData);
          profileUpdated = true;
          console.log('[Google Sign-up] Profile updated successfully');
        } catch (updateErr: any) {
          console.log(`[Google Sign-up] Update attempt failed, retries left: ${retries - 1}`, updateErr.message);
          
          if (updateErr.message?.includes('User not found') && retries > 1) {
            // Profile doesn't exist yet, wait and retry
            await new Promise(resolve => setTimeout(resolve, 500));
            retries--;
          } else {
            // Different error or no more retries
            throw updateErr;
          }
        }
      }
      
      if (!profileUpdated) {
        throw new Error('Failed to complete profile setup. Please update your profile from the account menu.');
      }
      
      setSuccess(t.loginForm.accountCreated);
    } catch (err: any) {
      console.error('Google Sign-up error:', err);
      setError(getAuthErrorMessage(t, err, t.loginForm.googleSignUpFailed));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update profile function
  const handleProfileUpdate = async () => {
    if (!isAuthenticated || !currentUser) {
      setError(t.loginForm.mustBeLoggedIn);
      return;
    }

    if (!fullName) {
      setError(t.loginForm.fullNameRequired);
      return;
    }

    if (!connection) {
      const projectName = tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center';
      setError(`${t.loginForm.connectionRequiredPrefix} ${projectName}`);
      return;
    }

    if (connection === 'Other' && !otherConnection) {
      const projectName = tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center';
      setError(`${t.loginForm.connectionSpecifyPrefix} ${projectName}`);
      return;
    }

    // Evanston-specific validation
    if (tenantConfig?.tenant_id === 'evanston') {
      if (!zipCode) {
        setError(t.loginForm.zipRequired);
        return;
      }

      if (!/^\d{5}$/.test(zipCode)) {
        setError(t.loginForm.zipFormat);
        return;
      }

      if (!ward) {
        setError(t.loginForm.wardRequired);
        return;
      }

      if (!raceEthnicity) {
        setError(t.loginForm.raceEthnicityRequired);
        return;
      }

      if (raceEthnicity === 'Other' && !otherRaceEthnicity) {
        setError(t.loginForm.raceEthnicitySpecifyRequired);
        return;
      }
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      const connectionValue = connection === 'Other' ? otherConnection : connection;
      
      // Build profile update object
      const profileUpdate: any = {
        displayName: fullName,
        connection: connectionValue
      };
      
      // Add Evanston-specific fields if applicable
      if (tenantConfig?.tenant_id === 'evanston') {
        profileUpdate.zip_code = zipCode;
        profileUpdate.ward = ward;
        profileUpdate.race_ethnicity = raceEthnicity === 'Other' ? otherRaceEthnicity : raceEthnicity;
        profileUpdate.address = address.trim();
      }

      // Update profile using the context function
      await updateUserProfile(profileUpdate);
      
      setSuccess(t.loginForm.profileUpdated);
      setShowProfileCompletion(false); // Hide profile completion form after successful update
      
      // Close the modal if this was a first-time profile completion
      if (isFirstTimeProfileUpdate && onClose) {
        setTimeout(() => {
          onClose();
        }, 2000); // Give user time to see the success message
      }
      
      // Reset the first-time flag after update
      setIsFirstTimeProfileUpdate(false);
    } catch (err: any) {
      console.error('Profile update error:', err);
      setError(err.message || t.loginForm.profileUpdateFailed);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      api.setAuthToken(null);
      setSuccess(t.loginForm.loggedOut);
    } catch (err: any) {
      console.error('Logout error:', err);
      setError(err.message || t.loginForm.logoutFailed);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Paper sx={{ p: 0, width: '100%', maxWidth: 400, mx: 'auto', position: 'relative' }}>
      {/* Close button in top right corner */}
      {onClose && (
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            backgroundColor: 'background.paper',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
      
      {isAuthenticated ? (
        <Box sx={{ p: 3, pt: onClose ? 5 : 3 }}>
          <Typography variant="h6" gutterBottom>
            {t.loginForm.loggedIn}
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            {t.loginForm.loggedInAsPrefix} {userProfile?.displayName || currentUser?.email}
            {userProfile?.connection && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t.loginForm.connectionPrefix} {translateConnectionOption(t, userProfile.connection)}
              </Typography>
            )}
          </Alert>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          
          {showProfileCompletion && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t.loginForm.completeProfileTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t.loginForm.completeProfileHelpPrefix} {tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center'} {t.loginForm.completeProfileHelpSuffix}
              </Typography>
              
              <TextField
                label={t.loginForm.fullName}
                fullWidth
                margin="normal"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={isSubmitting}
              />
              
              <FormControl fullWidth margin="normal">
                <InputLabel id="connection-label-update">
                  {t.loginForm.connectionToPrefix} {tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center'}
                </InputLabel>
                <Select
                  labelId="connection-label-update"
                  value={connection}
                  label="Connection to Severance"
                  onChange={handleConnectionChange}
                  disabled={isSubmitting}
                >
                  {connectionOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {translateConnectionOption(t, option)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {connection === 'Other' && (
                <TextField
                  label={t.loginForm.pleaseSpecify}
                  fullWidth
                  margin="normal"
                  value={otherConnection}
                  onChange={(e) => setOtherConnection(e.target.value)}
                  disabled={isSubmitting}
                />
              )}
              
              {/* Evanston-specific fields */}
              {tenantConfig?.tenant_id === 'evanston' && (
                <EvanstonFields
                  idSuffix="complete"
                  t={t}
                  disabled={isSubmitting}
                  zipCode={zipCode}
                  setZipCode={setZipCode}
                  ward={ward}
                  setWard={setWard}
                  address={address}
                  setAddress={setAddress}
                  raceEthnicity={raceEthnicity}
                  setRaceEthnicity={setRaceEthnicity}
                  otherRaceEthnicity={otherRaceEthnicity}
                  setOtherRaceEthnicity={setOtherRaceEthnicity}
                />
              )}

              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={isSubmitting}
                onClick={handleProfileUpdate}
                sx={{ mt: 2 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : t.loginForm.completeProfileButton}
              </Button>
            </Box>
          )}
          
          {!showProfileCompletion && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t.loginForm.updateProfileTitle}
              </Typography>
              
              <TextField
                label={t.loginForm.fullName}
                fullWidth
                margin="normal"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={isSubmitting}
              />
              
              <FormControl fullWidth margin="normal">
                <InputLabel id="connection-label-update">
                  {t.loginForm.connectionToPrefix} {tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center'}
                </InputLabel>
                <Select
                  labelId="connection-label-update"
                  value={connection}
                  label="Connection to Severance"
                  onChange={handleConnectionChange}
                  disabled={isSubmitting}
                >
                  {connectionOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {translateConnectionOption(t, option)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {connection === 'Other' && (
                <TextField
                  label={t.loginForm.pleaseSpecify}
                  fullWidth
                  margin="normal"
                  value={otherConnection}
                  onChange={(e) => setOtherConnection(e.target.value)}
                  disabled={isSubmitting}
                />
              )}
              
              {/* Evanston-specific fields */}
              {tenantConfig?.tenant_id === 'evanston' && (
                <EvanstonFields
                  idSuffix="update"
                  t={t}
                  disabled={isSubmitting}
                  zipCode={zipCode}
                  setZipCode={setZipCode}
                  ward={ward}
                  setWard={setWard}
                  address={address}
                  setAddress={setAddress}
                  raceEthnicity={raceEthnicity}
                  setRaceEthnicity={setRaceEthnicity}
                  otherRaceEthnicity={otherRaceEthnicity}
                  setOtherRaceEthnicity={setOtherRaceEthnicity}
                />
              )}
              
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={isSubmitting}
                onClick={handleProfileUpdate}
                sx={{ mt: 2 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : t.loginForm.updateProfileButton}
              </Button>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onClose && (
              <Button 
                variant="contained" 
                color="primary" 
                fullWidth 
                onClick={onClose}
              >
                {t.loginForm.continue}
              </Button>
            )}
            <Button 
              variant="outlined" 
              color="primary" 
              fullWidth 
              onClick={handleLogout}
            >
              {t.loginForm.logOut}
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', pt: onClose ? 5 : 0 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="auth tabs" variant="fullWidth">
              <Tab label={t.loginForm.loginTab} {...a11yProps(0)} />
              <Tab label={t.loginForm.signUpTab} {...a11yProps(1)} />
            </Tabs>
          </Box>
          
          <TabPanel value={tabValue} index={0}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleLogin}>
              <TextField
                label={t.loginForm.email}
                type="email"
                fullWidth
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
              
              <TextField
                label={t.loginForm.password}
                type="password"
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
              />
              
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isSubmitting}
                sx={{ mt: 2 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : t.loginForm.loginButton}
              </Button>

              <Button
                variant="text"
                color="primary"
                fullWidth
                size="small"
                onClick={handleForgotPassword}
                disabled={isSubmitting}
                sx={{ mt: 1 }}
              >
                {t.loginForm.forgotPassword}
              </Button>

              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">{t.loginForm.or}</Typography>
              </Divider>
              
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<GoogleIcon />}
                onClick={handleGoogleSignIn}
                disabled={isSubmitting}
              >
                {t.loginForm.signInWithGoogle}
              </Button>
              
              <Button
                variant="text"
                color="primary"
                fullWidth
                onClick={() => setTabValue(1)}
                sx={{ mt: 1 }}
              >
                {t.loginForm.needAccount}
              </Button>
            </Box>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleSignup}>
              <TextField
                label={t.loginForm.fullName}
                fullWidth
                margin="normal"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={isSubmitting}
                required
                helperText={t.loginForm.fullNameHelper}
              />
              
              <TextField
                label={t.loginForm.email}
                type="email"
                fullWidth
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                required
              />
              
              <TextField
                label={t.loginForm.password}
                type="password"
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                required
                helperText={t.loginForm.passwordHelper}
              />
              
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="connection-label">
                  {t.loginForm.connectionToPrefix} {tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center'}
                </InputLabel>
                <Select
                  labelId="connection-label"
                  value={connection}
                  label={`${t.loginForm.connectionToPrefix} ${tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center'}`}
                  onChange={handleConnectionChange}
                  disabled={isSubmitting}
                >
                  {connectionOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {translateConnectionOption(t, option)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {connection === 'Other' && (
                <TextField
                  label={t.loginForm.pleaseSpecify}
                  fullWidth
                  margin="normal"
                  value={otherConnection}
                  onChange={(e) => setOtherConnection(e.target.value)}
                  disabled={isSubmitting}
                  required
                />
              )}
              
              {/* Evanston-specific fields */}
              {tenantConfig?.tenant_id === 'evanston' && (
                <EvanstonFields
                  idSuffix="signup"
                  t={t}
                  disabled={isSubmitting}
                  required
                  zipCode={zipCode}
                  setZipCode={setZipCode}
                  ward={ward}
                  setWard={setWard}
                  address={address}
                  setAddress={setAddress}
                  raceEthnicity={raceEthnicity}
                  setRaceEthnicity={setRaceEthnicity}
                  otherRaceEthnicity={otherRaceEthnicity}
                  setOtherRaceEthnicity={setOtherRaceEthnicity}
                />
              )}
              
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isSubmitting}
                sx={{ mt: 2 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : t.loginForm.signUpButton}
              </Button>
              
              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">{t.loginForm.or}</Typography>
              </Divider>
              
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<GoogleIcon />}
                onClick={handleGoogleSignUp}
                disabled={isSubmitting}
              >
                {t.loginForm.signUpWithGoogle}
              </Button>
              
              <Button
                variant="text"
                color="primary"
                fullWidth
                onClick={() => setTabValue(0)}
                sx={{ mt: 1 }}
              >
                {t.loginForm.haveAccount}
              </Button>
            </Box>
          </TabPanel>
        </>
      )}
      
      {/* Snackbar for validation messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="warning" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default LoginForm; 